<template>
  <div style="padding: 20px;">
    <h2>Token Refresh Test</h2>

    <div style="margin: 20px 0;">
      <h3>Current Session Info:</h3>
      <pre>{{ sessionInfo }}</pre>
    </div>

    <div style="margin: 20px 0;">
      <button @click="checkSession" style="margin-right: 10px; padding: 10px 20px;">
        Check Session
      </button>

      <button @click="corruptToken" style="margin-right: 10px; padding: 10px 20px; background: orange; color: white;">
        Corrupt Token (Simulate Expiry)
      </button>

      <button @click="testApiCall" style="margin-right: 10px; padding: 10px 20px; background: green; color: white;">
        Test API Call
      </button>

      <button @click="fixToken" style="padding: 10px 20px; background: blue; color: white;">
        Restore Token
      </button>
    </div>

    <div style="margin: 20px 0;">
      <h3>Test Result:</h3>
      <pre>{{ testResult }}</pre>
    </div>

    <div style="margin: 20px 0; padding: 10px; background: #f0f0f0;">
      <h4>Instructions:</h4>
      <ol>
        <li>Click "Check Session" to see your current token info</li>
        <li>Click "Corrupt Token" to simulate token expiry</li>
        <li>Click "Test API Call" to trigger a 401 error and watch auto-refresh</li>
        <li>Watch browser console for detailed logs</li>
        <li>Click "Restore Token" if you need to fix it manually</li>
      </ol>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TestTokenRefresh',
  data() {
    return {
      sessionInfo: {},
      testResult: 'No test run yet',
      originalSession: null
    }
  },
  mounted() {
    this.checkSession()
  },
  methods: {
    checkSession() {
      const storedSessionStr = localStorage.getItem('supabase_session')
      const storedToken = localStorage.getItem('auth_token')

      if (storedSessionStr) {
        try {
          const session = JSON.parse(storedSessionStr)
          this.sessionInfo = {
            hasAccessToken: !!session.access_token,
            hasRefreshToken: !!session.refresh_token,
            accessTokenLength: session.access_token ? session.access_token.length : 0,
            refreshTokenLength: session.refresh_token ? session.refresh_token.length : 0,
            expiresAt: session.expires_at ? new Date(session.expires_at * 1000).toLocaleString() : 'N/A'
          }

          // Backup original session
          this.originalSession = storedSessionStr
        } catch (e) {
          this.sessionInfo = { error: 'Failed to parse session' }
        }
      } else {
        this.sessionInfo = {
          message: 'No session found',
          storedToken: storedToken ? 'Token exists' : 'No token'
        }
      }
    },

    corruptToken() {
      const storedSessionStr = localStorage.getItem('supabase_session')

      if (!storedSessionStr) {
        this.testResult = 'ERROR: No session to corrupt. Please login first.'
        return
      }

      // Backup original
      this.originalSession = storedSessionStr

      try {
        const session = JSON.parse(storedSessionStr)

        // Corrupt the access token to simulate expiry
        session.access_token = session.access_token + '_CORRUPTED_TO_SIMULATE_EXPIRY'

        // Also set expiry to past
        session.expires_at = Math.floor(Date.now() / 1000) - 3600

        // Save corrupted session
        localStorage.setItem('supabase_session', JSON.stringify(session))
        localStorage.setItem('auth_token', session.access_token)

        // Update store
        this.$store.commit('auth/setSession', session)

        this.testResult = '✅ Token corrupted! Now click "Test API Call" to trigger 401 and watch auto-refresh in console.'
        this.checkSession()
      } catch (e) {
        this.testResult = 'ERROR: ' + e.message
      }
    },

    async testApiCall() {
      this.testResult = '🔄 Making API call... Watch console for refresh logs!'

      try {
        // Make a real API call that requires auth
        const response = await axios.get(`${process.env.VUE_APP_API_ENDPOINT}/api/scans/scan-stats`)

        this.testResult = '✅ API call succeeded!\n' + JSON.stringify(response.data, null, 2)
        this.checkSession()
      } catch (error) {
        this.testResult = '❌ API call failed: ' + error.message + '\n\nCheck console for refresh attempt logs.'
      }
    },

    fixToken() {
      if (this.originalSession) {
        localStorage.setItem('supabase_session', this.originalSession)
        const session = JSON.parse(this.originalSession)
        localStorage.setItem('auth_token', session.access_token)
        this.$store.commit('auth/setSession', session)

        this.testResult = '✅ Token restored from backup'
        this.checkSession()
      } else {
        this.testResult = '❌ No backup available. Please login again.'
      }
    }
  }
}
</script>
</template>

<style scoped>
pre {
  background: #f5f5f5;
  padding: 10px;
  border-radius: 4px;
  overflow: auto;
}

button {
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
}

button:hover {
  opacity: 0.8;
}
</style>
